import { createContext, useContext, useEffect, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import {
    RequestStatus,
    useServerFetch,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { useUpdate } from 'dg-web-shared/lib/Flux.tsx';
import { logout } from '../actions/AuthenticationActions.ts';

export type ReadOnlyExportContextState = Readonly<{
    runningExportId: string | null;
    newDownloads: number;
    downloadList: ReadOnlyExportDownlaodItem[];
    refetchReadOnlyExportData: () => void;
}>;

export interface ReadOnlyExportDownlaodItem {
    readOnlyExportId: string;
    createdAt: string;
    typeName: Message;
    state: ReadOnlyDownloadState;
}

export enum ReadOnlyDownloadState {
    BEING_CREATED = 'BEING_CREATED',
    READY = 'READY',
    DOWNLOADED = 'DOWNLOADED',
}

export const ReadOnlyExportContext = createContext<ReadOnlyExportContextState>({
    runningExportId: null,
    newDownloads: 0,
    downloadList: [],
    refetchReadOnlyExportData: () => {},
});

export function useReadOnlyExportContext() {
    return useContext(ReadOnlyExportContext);
}

interface ReadOnlyExportData {
    runningExportId: string | null;
    newDownloads: number;
    downloadList: [];
}

export function ReadOnlyExportContextProvider() {
    const [readOnlyExportState, refetchReadOnlyExportData] = useServerFetch<
        ReadOnlyExportData,
        object
    >(
        () => ({
            url: `/ui-api/operator-account/read-only-export-running`,
        }),
        {},
    );

    const update = useUpdate();
    useEffect(() => {
        if (
            readOnlyExportState.status === RequestStatus.ERROR &&
            readOnlyExportState.httpStatusCode === 401
        ) {
            update(logout);
        }
    }, [update, readOnlyExportState]);

    useEffect(() => {
        const interval = setInterval(() => {
            refetchReadOnlyExportData();
        }, 60000);
        return () => clearInterval(interval);
    }, [refetchReadOnlyExportData]);

    const readOnlyExportContextValue = useMemo(
        () => ({
            runningExportId: readOnlyExportState.data?.runningExportId ?? null,
            newDownloads: readOnlyExportState.data?.newDownloads ?? 0,
            downloadList: readOnlyExportState.data?.downloadList ?? [],
            refetchReadOnlyExportData,
        }),
        [readOnlyExportState.data, refetchReadOnlyExportData],
    );

    return (
        <ReadOnlyExportContext.Provider value={readOnlyExportContextValue}>
            <Outlet />
        </ReadOnlyExportContext.Provider>
    );
}
